// Clients B2B — pure logic shared by the list (ClientsInstructions.js), the
// create modal and the client page (ClientEDNDetail.js). No React, no fetch.
//
// One field table drives the three places a B2B client field appears (create
// modal, edit form, read-only preview), so a field can never exist in one and
// be missing from another (frontend-repo rule « new client fields must be
// added to BOTH files »). Shapes: Backend app/api/logistics.py — ClientEDN,
// CreateClientEDNRequest, UpdateClientEDNRequest, ClientEDNStatsResponse.
import { getStatusClass, TERMINAL_READ_STATUS_VALUES } from '../constants/ticketStatuses';
import { parseUtc } from '../utils/dates';

// Stored in English: the backend AI prompt consumes the value directly.
export const SOUFFRANCE_LANGUAGES = [
  'French',
  'English',
  'German',
  'Spanish',
  'Italian',
  'Dutch',
  'Portuguese',
  'Polish',
];

// Every editable field of a clients_edn document. `label` / `help` are the
// French defaults of the i18n keys (clientsUi.fields.<key>.label / .help).
// `payload` names where the field travels in the create / update request.
export const B2B_FIELDS = {
  name: { kind: 'text', required: true, label: 'Nom du client', payload: 'name' },
  client_id: { kind: 'text', label: 'Identifiant client', help: 'Le code du client dans votre ERP ou votre TMS : un ticket qui le porte est rattaché à ce client.', payload: 'client_id' },
  reference: { kind: 'text', label: 'Référence', help: 'Une référence interne, libre.', payload: 'reference' },
  aliases: { kind: 'list', label: 'Alias et enseignes', help: 'Les autres noms sous lesquels ce client apparaît (enseigne, nom commercial) : un ticket qui en porte un lui est rattaché.', placeholder: 'Nom ou enseigne', payload: 'aliases' },
  emails: { kind: 'list', label: 'Adresses et domaines e-mail', help: 'Les e-mails entrants venant de ces adresses ou de ces domaines (ex. marque.com) sont rattachés à ce client.', placeholder: 'nom@marque.com ou marque.com', payload: 'emails' },
  instructions: { kind: 'textarea', label: 'Consignes pour l’agent', help: 'Ce que l’agent doit savoir ou faire pour les tickets de ce client. Elles s’ajoutent à vos consignes générales.', payload: 'instructions' },
  effitrace_url: { kind: 'url', label: 'Portail EffiTrace des commandes', help: 'Adresse du portail EffiTrace où l’agent retrouve les commandes de ce client.', placeholder: 'https://…', payload: 'effitrace_url' },
  automation: { kind: 'switch', label: 'Automatisation', help: 'Active : l’agent IA traite les tickets de ce client et agit seul. Désactivée : ses propositions attendent votre équipe.', payload: 'automation' },
  disabled_carriers: { kind: 'carriers', label: 'Transporteurs exclus', help: 'L’automatisation n’utilise pas ces transporteurs pour ce client.', payload: 'disabled_carriers' },
  default_email: { kind: 'email', label: 'Adresse des avis de souffrance', help: 'Reçoit les avis de souffrance de ce client (colis en difficulté).', placeholder: 'logistique@marque.com', payload: 'email_routing' },
  b2b_email: { kind: 'email', advanced: true, label: 'Adresse pour les commandes B2B', help: 'Facultatif : remplace l’adresse ci-dessus pour les envois B2B.', placeholder: 'facultatif', payload: 'email_routing' },
  b2c_email: { kind: 'email', advanced: true, label: 'Adresse pour les commandes B2C', help: 'Facultatif : remplace l’adresse ci-dessus pour les envois aux particuliers.', placeholder: 'facultatif', payload: 'email_routing' },
  souffrance_language: { kind: 'language', label: 'Langue des avis', help: 'Remplace la langue de vos réglages généraux pour ce client.', payload: 'souffrance_language' },
  souffrance_instructions: { kind: 'textarea', label: 'Consignes des avis', help: 'S’ajoutent à vos consignes générales de souffrance pour ce client.', placeholder: 'Ex. toujours mentionner notre numéro de support…', payload: 'souffrance_instructions' },
};

export const B2B_FIELD_KEYS = Object.keys(B2B_FIELDS);

// Sections, in reading order. `title` / `desc` = French defaults of
// clientsUi.sections.<key>.title / .desc.
export const B2B_SECTIONS = [
  { key: 'identity', title: 'Identité', desc: 'Comment ce client est nommé, ici et dans vos outils.', fields: ['name', 'client_id', 'reference', 'aliases'] },
  { key: 'detection', title: 'Reconnaissance des e-mails', desc: 'Ce qui permet de rattacher un e-mail entrant à ce client.', fields: ['emails'] },
  { key: 'agent', title: 'Agent IA', desc: 'Ce que l’agent applique aux tickets de ce client, et où il trouve ses commandes.', fields: ['instructions', 'effitrace_url'] },
  { key: 'automation', title: 'Automatisation et transporteurs', desc: 'Ce que l’agent peut faire seul, et avec quels transporteurs.', fields: ['automation', 'disabled_carriers'] },
  { key: 'souffrance', title: 'Avis de souffrance', desc: 'Les avis envoyés à ce client quand un de ses colis est en difficulté.', fields: ['default_email', 'souffrance_language', 'b2b_email', 'b2c_email', 'souffrance_instructions'] },
];

export const ROUTING_KEYS = ['default_email', 'b2b_email', 'b2c_email'];

export const emptyB2BForm = () => ({
  name: '',
  client_id: '',
  reference: '',
  aliases: [],
  emails: [],
  instructions: '',
  effitrace_url: '',
  automation: false,
  disabled_carriers: [],
  default_email: '',
  b2b_email: '',
  b2c_email: '',
  souffrance_language: '',
  souffrance_instructions: '',
});

// The edit form of a stored client (same keys as emptyB2BForm).
export const formFromClient = (client) => {
  const routing = (client && client.email_routing) || {};
  return {
    name: client?.name || '',
    client_id: client?.client_id || '',
    reference: client?.reference || '',
    aliases: client?.aliases || [],
    emails: client?.emails || [],
    instructions: client?.instructions || '',
    effitrace_url: client?.effitrace_url || '',
    automation: client?.automation || false,
    disabled_carriers: client?.disabled_carriers || [],
    default_email: routing.default_email || '',
    b2b_email: routing.b2b_email || '',
    b2c_email: routing.b2c_email || '',
    souffrance_language: client?.souffrance_language || '',
    souffrance_instructions: client?.souffrance_instructions || '',
  };
};

const routingOf = (form) => {
  const routing = {};
  ROUTING_KEYS.forEach((key) => {
    if (form[key]) routing[key] = form[key];
  });
  return Object.keys(routing).length > 0 ? routing : null;
};

// POST /api/logistics/clients-edn (CreateClientEDNRequest) — the shape the
// page always sent; the B2B / B2C routing addresses join email_routing when
// they are filled (the create modal now has every field of the edit form).
export const buildCreatePayload = (form) => ({
  name: form.name.trim(),
  client_id: form.client_id,
  emails: form.emails.filter((email) => email.trim() !== ''),
  instructions: form.instructions,
  reference: form.reference,
  automation: form.automation,
  disabled_carriers: form.disabled_carriers,
  aliases: form.aliases.filter((alias) => alias.trim() !== ''),
  email_routing: routingOf(form),
  effitrace_url: form.effitrace_url || null,
  souffrance_language: form.souffrance_language || null,
  souffrance_instructions: form.souffrance_instructions || null,
});

// PUT /api/logistics/clients-edn/{id} (UpdateClientEDNRequest) — unchanged.
// The backend ignores null, so client_id / email_routing / effitrace_url
// cannot be cleared from here (described in the rework report).
export const buildUpdatePayload = (form) => ({
  name: form.name,
  client_id: form.client_id || null,
  emails: form.emails,
  instructions: form.instructions,
  reference: form.reference,
  automation: form.automation,
  disabled_carriers: form.disabled_carriers,
  aliases: form.aliases,
  email_routing: routingOf(form),
  effitrace_url: form.effitrace_url || null,
  // Empty strings (not null) so clearing a field actually persists.
  souffrance_language: form.souffrance_language ?? '',
  souffrance_instructions: form.souffrance_instructions ?? '',
});

// Add one entry to a list field: trimmed, never empty, never twice.
export const addListEntry = (list, value) => {
  const entry = String(value || '').trim();
  if (!entry) return list;
  const lower = entry.toLowerCase();
  if (list.some((item) => String(item).trim().toLowerCase() === lower)) return list;
  return [...list, entry];
};

const norm = (value) => String(value || '').trim().toLowerCase();

// The provenance of an alias the AI added after verifying the enseigne on a
// parcel (read-only, KI-257 suite).
export const aliasProvenanceOf = (client, alias) => (client?.alias_provenance || [])
  .find((entry) => norm(entry.alias) === norm(alias)) || null;

// Addresses vs domains among the detection entries.
export const detectionCounts = (emails) => {
  const entries = (emails || []).filter((entry) => String(entry || '').trim());
  const addresses = entries.filter((entry) => entry.includes('@')).length;
  return { total: entries.length, addresses, domains: entries.length - addresses };
};

const hasText = (value) => typeof value === 'string' && value.trim() !== '';

const DAY_MS = 86400000;
export const RECENT_ALIAS_DAYS = 14;

const isRecent = (value, now, days) => {
  const date = parseUtc(value);
  return Boolean(date) && now - date.getTime() <= days * DAY_MS;
};

// Disabled carriers the tenant no longer has (the edit form keeps them
// selectable so they can be removed).
export const unknownDisabledCarriers = (client, carriers) => {
  if (!Array.isArray(carriers) || carriers.length === 0) return [];
  const known = new Set(carriers.map((carrier) => norm(carrier.name)));
  return (client?.disabled_carriers || []).filter((name) => !known.has(norm(name)));
};

// List filters (?filter=): the segmented ones and those the tiles and
// « À vérifier » lead to.
export const LIST_FILTERS = ['all', 'auto', 'manual', 'noemail', 'noinstr', 'ai_alias', 'excluded'];

export const matchesFilter = (client, filter) => {
  switch (filter) {
    case 'auto': return Boolean(client.automation);
    case 'manual': return !client.automation;
    case 'noemail': return detectionCounts(client.emails).total === 0;
    case 'noinstr': return !hasText(client.instructions);
    case 'ai_alias': return (client.alias_provenance || []).length > 0;
    case 'excluded': return (client.disabled_carriers || []).length > 0;
    default: return true;
  }
};

// Search: name, client id, reference, aliases, e-mails.
export const matchesSearch = (client, query) => {
  const q = norm(query);
  if (!q) return true;
  return [client.name, client.client_id, client.reference, ...(client.aliases || []), ...(client.emails || [])]
    .some((value) => norm(value).includes(q));
};

// Numbers of the list header and tiles — all from GET /clients-edn.
export const b2bListSummary = (clients, now = Date.now()) => {
  const list = clients || [];
  const count = (filter) => list.filter((client) => matchesFilter(client, filter)).length;
  const aiAliases = list.flatMap((client) => (client.alias_provenance || []).map((entry) => ({ client, entry })));
  return {
    total: list.length,
    automated: count('auto'),
    manual: count('manual'),
    withEmails: list.length - count('noemail'),
    noEmail: count('noemail'),
    withInstructions: list.length - count('noinstr'),
    noInstructions: count('noinstr'),
    automatedNoInstructions: list.filter((client) => client.automation && !hasText(client.instructions)).length,
    excluded: count('excluded'),
    aiAliasClients: count('ai_alias'),
    aiAliases: aiAliases.length,
    recentAiAliases: aiAliases.filter(({ entry }) => isRecent(entry.at, now, RECENT_ALIAS_DAYS)),
    ownSouffrance: list.filter((client) => hasText(client.souffrance_language) || hasText(client.souffrance_instructions)).length,
  };
};

// What on the list needs a person, blocking first. Each item leads to a
// filter of the list (or to the client when there is one).
export const b2bAttention = (clients, carriers, now = Date.now()) => {
  const list = clients || [];
  const summary = b2bListSummary(list, now);
  const items = [];
  if (summary.noEmail > 0) {
    items.push({
      key: 'noemail', tone: 'warning', count: summary.noEmail, filter: 'noemail',
      names: list.filter((client) => matchesFilter(client, 'noemail')).map((client) => client.name),
    });
  }
  if (summary.automatedNoInstructions > 0) {
    items.push({
      key: 'noinstr', tone: 'warning', count: summary.automatedNoInstructions, filter: 'noinstr',
      names: list.filter((client) => client.automation && !hasText(client.instructions)).map((client) => client.name),
    });
  }
  if (summary.recentAiAliases.length > 0) {
    items.push({
      key: 'ai_alias', tone: 'info', count: summary.recentAiAliases.length, filter: 'ai_alias',
      names: summary.recentAiAliases.map(({ client, entry }) => `${client.name} : « ${entry.alias} »`),
    });
  }
  list.forEach((client) => {
    const unknown = unknownDisabledCarriers(client, carriers);
    if (unknown.length) {
      items.push({ key: `stale-${client.id}`, tone: 'warning', kind: 'stale', clientId: client.id, clientName: client.name, carriers: unknown });
    }
  });
  return items.map((item) => ({ kind: item.kind || item.key, ...item }));
};

// The tenant's general souffrance settings are « personalised » when any of
// the three values is set (GET /souffrance-config).
export const souffranceConfigured = (config) => Boolean(
  config && (hasText(config.language) || hasText(config.instructions) || hasText(config.signature)),
);

// ---- One client: activity from GET /clients-edn/{id}/stats ----------------

// Status → bucket of the composition bar (same semantic classes as the
// ticket lists: violet new, blue in progress, orange waiting, green done).
const BUCKET_BY_CLASS = {
  'status-new': 'new',
  'status-open': 'progress',
  'status-processing': 'progress',
  'status-automated': 'progress',
  'status-waiting': 'waiting',
  'status-pending': 'waiting',
  'status-resolved': 'resolved',
  'status-closed': 'resolved',
};
export const STATUS_BUCKETS = ['new', 'progress', 'waiting', 'resolved', 'other'];
export const bucketOf = (status) => BUCKET_BY_CLASS[getStatusClass(status)] || 'other';

// Badge tone of a ticket status (processing_error is the only blocking one).
const TONE_BY_BUCKET = { new: 'accent', progress: 'info', waiting: 'warning', resolved: 'success' };
export const statusTone = (status) => (status === 'processing_error' ? 'danger' : TONE_BY_BUCKET[bucketOf(status)]);

const WAITING_CARRIER = ['waiting_carrier_response', 'waiting_carrier_commercial_response'];
const NEEDS_TEAM = ['action_required', 'processing_error'];

export const clientActivity = (stats) => {
  if (!stats) return null;
  const total = Number(stats.total_tickets) || 0;
  const byStatus = (stats.tickets_by_status || [])
    .filter((row) => row && row.status && Number(row.count) > 0)
    .map((row) => ({ status: row.status, count: Number(row.count), bucket: bucketOf(row.status) }))
    .sort((a, b) => b.count - a.count);
  const sum = (predicate) => byStatus.filter(predicate).reduce((acc, row) => acc + row.count, 0);
  const resolved = sum((row) => TERMINAL_READ_STATUS_VALUES.includes(row.status));
  const open = sum((row) => !TERMINAL_READ_STATUS_VALUES.includes(row.status));
  const buckets = STATUS_BUCKETS
    .map((bucket) => ({ bucket, count: sum((row) => row.bucket === bucket) }))
    .filter((row) => row.count > 0);
  const carriers = (stats.top_carriers || []).filter((row) => row && row.carrier_name && Number(row.count) > 0);
  const topics = (stats.topics_distribution || []).filter((row) => row && row.topic && Number(row.count) > 0);
  return {
    total,
    open,
    resolved,
    waitingCarrier: sum((row) => WAITING_CARRIER.includes(row.status)),
    needsTeam: sum((row) => NEEDS_TEAM.includes(row.status)),
    byStatus,
    buckets,
    carriers,
    carriersMax: Math.max(1, ...carriers.map((row) => Number(row.count))),
    topics,
    topicsMax: Math.max(1, ...topics.map((row) => Number(row.count))),
    topCarrier: carriers[0] || null,
  };
};

// What on one client needs a person (client page « À vérifier »).
export const clientAttention = ({ client, carriers, apiAccess, activity, now = Date.now() }) => {
  if (!client) return [];
  const items = [];
  if (activity && activity.needsTeam > 0) items.push({ key: 'team', tone: 'danger', tab: 'tickets', status: 'action_required', count: activity.needsTeam });
  if (detectionCounts(client.emails).total === 0) items.push({ key: 'noemail', tone: 'warning', tab: 'configuration' });
  if (client.automation && !hasText(client.instructions)) items.push({ key: 'noinstr', tone: 'warning', tab: 'configuration' });
  const stale = unknownDisabledCarriers(client, carriers);
  if (stale.length) items.push({ key: 'stale', tone: 'warning', tab: 'configuration', carriers: stale });
  (client.alias_provenance || [])
    .filter((entry) => isRecent(entry.at, now, RECENT_ALIAS_DAYS))
    .forEach((entry) => items.push({ key: `alias-${entry.alias}`, kind: 'alias', tone: 'info', tab: 'configuration', entry }));
  if (apiAccess?.enabled && !apiAccess.last_used_at) items.push({ key: 'apiunused', tone: 'info', tab: 'api' });
  return items.map((item) => ({ kind: item.kind || item.key, ...item }));
};

// GET /api/statistics/overview → top_clients, matched to the pages by name
// or alias (case-insensitive; the stats group tickets by client name).
export const matchTopClients = (topClients, clients) => {
  const rows = (topClients && topClients.clients) || [];
  const index = new Map();
  (clients || []).forEach((client) => {
    [client.name, ...(client.aliases || [])].forEach((name) => {
      if (norm(name) && !index.has(norm(name))) index.set(norm(name), client);
    });
  });
  const max = Math.max(1, ...rows.map((row) => Number(row.total) || 0));
  return rows.map((row) => {
    const total = Number(row.total) || 0;
    return {
      name: row.client,
      total,
      resolved: Number(row.resolved) || 0,
      aiResolved: Number(row.ai_resolved) || 0,
      resolutionRate: total ? (Number(row.resolved) || 0) / total : null,
      aiShare: total ? (Number(row.ai_resolved) || 0) / total : null,
      share: Number(row.percentage) || 0,
      max,
      client: index.get(norm(row.client)) || null,
    };
  });
};
